import { Select } from '@mantine/core'
import type {
  TKcOverrideType,
  TOrganizationKcModel,
  TSelectedKcConfiguration,
  TSelectedKcConfigurationMap,
  TSemiosKcModel,
} from 'App/Map/UserSettingsMenu/Shared/EvapotranspirationSettings/types'
import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import type { Dispatch, SetStateAction } from 'react'
import { alphabeticalSort } from 'utils/alphabeticalSort'

export const KcModelSelect = ({
  blockId,
  loading,
  selectedValue,
  semiosKcModels,
  setSelectedValueMap,
  setDirtyBlockIds,
  organizationModelsData,
}: {
  blockId: number
  blockIdsOfOtherValidCropBlocks: number[]
  cropName: string
  isDirty: boolean
  loading: boolean
  organizationModelsData: TOrganizationKcModel[]
  selectedValue: TSelectedKcConfiguration
  semiosKcModels: TSemiosKcModel[]
  setDirtyBlockIds: Dispatch<SetStateAction<number[]>>
  setSelectedValueMap: Dispatch<SetStateAction<TSelectedKcConfigurationMap>>
}) => {
  const modelTypeMap = [...organizationModelsData, ...semiosKcModels].reduce<Record<string, TKcOverrideType>>(
    (acc, curr) => {
      return {
        ...acc,
        [String(curr.modelId)]: curr.modelType,
      }
    },
    {},
  )

  const toSelectItems = (models: (TOrganizationKcModel | TSemiosKcModel)[]) =>
    models
      .map(({ modelId, modelName }) => ({ value: modelId, label: modelName }))
      .sort((a, b) => alphabeticalSort(a.label, b.label))

  const isSemiosModel = ({ modelName }: TSemiosKcModel) => modelName.toLowerCase().startsWith('semios')

  const groups = [
    {
      group: translate.phrases.banyanApp('Organization Defined Models'),
      items: toSelectItems(organizationModelsData),
    },
    {
      group: translate.phrases.banyanApp('Semios Defined Models'),
      items: toSelectItems(semiosKcModels.filter(isSemiosModel)),
    },
    {
      group: translate.phrases.banyanApp('Other Models'),
      items: toSelectItems(semiosKcModels.filter((model) => !isSemiosModel(model))),
    },
  ].filter(({ items }) => items.length > 0)

  // If there is only one group, don't show the group label
  const selectData = groups.length > 1 ? groups : groups.flatMap(({ items }) => items)

  return (
    <div css={{ minWidth: 160 }}>
      <Select
        onClick={(e) => {
          e.stopPropagation()
        }}
        css={{ width: 'auto !important', maxWidth: '100%' }}
        disabled={loading || semiosKcModels.length === 0}
        value={selectedValue?.modelId}
        onChange={async (modelId) => {
          if (isNil(modelId)) return

          setDirtyBlockIds((prev) => [...prev, blockId])

          setSelectedValueMap((prev) => {
            const foundModelType = modelTypeMap[modelId]

            return {
              ...prev,
              [blockId]: {
                ...prev[blockId],
                modelId,
                modelType: foundModelType,
              },
            }
          })
        }}
        data={selectData}
      />
    </div>
  )
}
